/**
 * PdfServiceModule — V7.2.B (2026-06-03).
 *
 * Wraps the briefing-room backend's PDF library REST API.
 *
 * Routes (per briefing_room_backend/routes/pdfs.py):
 *   GET    /api/modules/briefing-room/pdfs              → PdfMeta[]
 *   GET    /api/modules/briefing-room/pdfs/{path:path}  → application/pdf bytes
 *
 * Auth: Bearer JWT from localStorage (TOKEN_KEY shared with vibe-designer).
 * Both routes JWT-gated. The stream route is NOT in PUBLIC_PATH_PREFIXES
 * (R1.0 Concern 1) — instead, fetchPdfBlob() carries the Bearer, returns
 * a Blob, and the caller (PdfViewerModal) uses URL.createObjectURL(blob)
 * so the iframe loads from a `blob:` URL.
 *
 * Constructor accepts an optional fetchFn for testability — vitest mocks
 * use this pattern across the host services suite (mirrors
 * PersistenceServiceModule.ts:30-94).
 */
import { getAuthHeaders } from './authHeaders';

const PDFS_PATH = '/api/modules/briefing-room/pdfs';

export interface PdfMeta {
  path: string;
  title: string;
  category: string;
  size_mb: number;
}

export class PdfServiceModule {
  constructor(
    private readonly basePath: string = PDFS_PATH,
    // Arrow-wrap the default to avoid "Illegal invocation" when fetch
    // is called as `this.fetchFn(...)` (browser fetch requires this===window).
    // Mirrors PersistenceServiceModule.ts:34-38 explicit comment.
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  isAvailable(): boolean {
    return true;
  }

  async listPdfs(): Promise<PdfMeta[]> {
    const resp = await this.fetchFn(this.basePath, {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(`PdfServiceModule: listPdfs HTTP ${resp.status}`);
    }
    return (await resp.json()) as PdfMeta[];
  }

  /**
   * Fetch a PDF as a binary blob with Bearer auth.
   *
   * The path is URL-encoded per segment so `#` (in `DW#46.pdf`),
   * `{}` (in `{VG35000}`), and spaces all survive transport. Per-segment
   * (not whole-path) so the `/` between MANUALS/MODULES and filename
   * stays unencoded.
   *
   * Returns a Blob the caller can pass to URL.createObjectURL() and
   * use as an iframe src. Caller MUST URL.revokeObjectURL() on
   * cleanup or the blob leaks (memory bound = blob size; one open
   * PDF = one blob, modal lifecycle releases on unmount).
   */
  async fetchPdfBlob(path: string): Promise<Blob> {
    const encoded = path
      .split('/')
      .map((segment) => encodeURIComponent(segment))
      .join('/');
    const url = `${this.basePath}/${encoded}`;
    const resp = await this.fetchFn(url, {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(
        `PdfServiceModule: fetchPdfBlob HTTP ${resp.status} for ${path}`,
      );
    }
    return resp.blob();
  }
}
